import i18n from '@/i18n';
import { notificationService } from '@/services/notification/notification-service';
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { useDeleteItemMutation } from '../../data/useDeleteItemMutation';
import { computeDeleteMode } from '../../permissions/modes';
import type { FileManagerPermissionsState } from '../../permissions/useFileManagerPermissions';
import type { FileSystemItem } from '../../types';
import { describeItems } from '../../utils';
import type { FileManagerDetailsState } from './useFileManagerDetails';
import type { FileManagerNavigationState } from './useFileManagerNavigation';

// Pure: the context-menu Delete entry's label. A single item stays a plain, unadorned verb
// ("Delete") — matching "Copy name"'s own convention of never embedding the item's name in a menu
// label — while a multi-item target names the count so it reads distinctly from a single-item
// Delete. Deliberately NOT reused verbatim for DeleteConfirmModal's title: that needs the richer,
// breakdown-aware phrasing the plan doc calls for (e.g. distinguishing files vs. folders, or naming
// a single item), which lives in the modal itself rather than here.
export const deleteMenuLabel = (target: FileSystemItem[]): string => {
  const { t } = i18n.global;
  const { total } = describeItems(target);
  return total === 1 ? t('fileManager.core.useFileManagerDelete.menuLabelSingle') : t('fileManager.core.useFileManagerDelete.menuLabelPlural', { count: total });
};

export type DeleteResult = { item: FileSystemItem; ok: boolean };

// Pure: turns a settled delete batch into a single aggregate message, or null when every item
// succeeded. Silent-success matches useMoveItemMutation's own convention (the confirm modal already
// told the user what was about to happen, so a second "it worked" toast would be noise). The single-
// failure case names the item (mirroring useMoveItemMutation's own single-item failure text); once
// more than one item fails, a count is used instead so the message never turns into an unreadable
// list of names.
export const summarizeDeleteFailures = (results: DeleteResult[]): string | null => {
  const { t } = i18n.global;
  const failed = results.filter((result) => !result.ok);
  if (failed.length === 0) return null;
  if (failed.length === 1) return t('fileManager.core.useFileManagerDelete.failedSingle', { name: failed[0].item.name });
  return t('fileManager.core.useFileManagerDelete.failedMultiple', { failedCount: failed.length, totalCount: results.length });
};

export type FileManagerDeleteState = {
  isConfirmOpen: Ref<boolean>;
  pendingTarget: Ref<FileSystemItem[]>;
  // Drives the dimmed in-flight state (DetailsTableView's `.is-deleting`), and —
  // since a failed item is removed from this set the moment its own promise settles, independent of
  // the others in the batch — lets that item un-dim and stay visibly distinct from ones that quietly
  // succeeded and disappeared once the refetch landed.
  deletingPaths: Ref<Set<string>>;
  /**
   * Whether Delete may be offered for an item at this path. Path-scoped rather than one ambient
   * boolean, because capabilities ride on the mount and the pane can show more than one project's
   * (D6). Every caller has the target in hand at the moment it builds the menu.
   */
  canDeleteAt: (path: string) => boolean;
  requestDelete: (target: FileSystemItem[]) => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
};

export const fileManagerDeleteKey: InjectionKey<FileManagerDeleteState> =
  Symbol('fileManagerDelete');

// `navigation`/`permissions`/`details` are all taken as explicit params rather than self-injected
// via useFileManagerNavigation()/useFileManagerPermissions()/useFileManagerDetails(): this factory
// is called once, directly in FileManager.vue's own setup() — the same instance that provides all
// three — and inject() resolves against the *parent* instance's provides, so a component can never
// inject its own provide() call (see provideFileManagerUpload's identical reasoning). This is a
// deliberate departure from useFileGridInteractions's own self-injection of useFileManagerPermissions
// — that composable is instead invoked from DetailsTableView, a genuine descendant
// of the component that provides permissions, where the same hazard doesn't apply.
export const provideFileManagerDelete = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
  permissions: Pick<FileManagerPermissionsState, 'permissionsAt'>,
  details: Pick<FileManagerDetailsState, 'detailsItem' | 'closeDetails'>,
) => {
  const mutation = useDeleteItemMutation(navigation);
  const { detailsItem, closeDetails } = details;

  const isConfirmOpen = ref(false);
  const pendingTarget = ref<FileSystemItem[]>([]);
  const deletingPaths = ref<Set<string>>(new Set());

  const canDeleteAt = (path: string) =>
    computeDeleteMode(permissions.permissionsAt(path)) === 'enabled';

  // Hide-on-missing is enforced up the call chain (context-menu builders / DetailsView omit the
  // Delete entry entirely when the target is not deletable), but this no-op guard is cheap insurance
  // against any stale entry point still calling through after a permission change. `every`, not the
  // first item: a selection is one folder's rows today, but nothing here depends on that.
  const requestDelete = (target: FileSystemItem[]) => {
    if (!target.every((item) => canDeleteAt(item.path))) return;
    pendingTarget.value = target;
    isConfirmOpen.value = true;
  };

  const cancelDelete = () => {
    isConfirmOpen.value = false;
  };

  const confirmDelete = () => {
    const target = pendingTarget.value;
    isConfirmOpen.value = false;
    deletingPaths.value = new Set(target.map((item) => item.path));

    const removeFromDeleting = (path: string) => {
      const next = new Set(deletingPaths.value);
      next.delete(path);
      deletingPaths.value = next;
    };

    // mutateAsync (not fire-and-forget mutate) specifically so this composable can await full
    // completion of the whole batch to aggregate one summary, rather than each item reporting for
    // itself. Each item is still removed from deletingPaths independently, via its own .finally, the
    // moment its own promise settles — it doesn't wait for the slowest item in the batch.
    Promise.allSettled(
      target.map((item) =>
        mutation
          .mutateAsync({ item })
          .then(
            (): DeleteResult => ({ item, ok: true }),
            (): DeleteResult => ({ item, ok: false }),
          )
          .finally(() => removeFromDeleting(item.path)),
      ),
    ).then((settled) => {
      // Every entry above already normalizes to a resolved { item, ok } pair via its own
      // .then(onFulfilled, onRejected) — none of these promises can actually reject, so
      // allSettled's outcomes are always 'fulfilled' here. flatMap guards for TypeScript's sake
      // without fabricating a fake result for a branch that can't be reached at runtime.
      const results = settled.flatMap((outcome) =>
        outcome.status === 'fulfilled' ? [outcome.value] : [],
      );

      const failureMessage = summarizeDeleteFailures(results);
      if (failureMessage) {
        notificationService.open({ type: 'danger', text: failureMessage });
      }

      const succeededPaths = new Set(
        results.filter((result) => result.ok).map((result) => result.item.path),
      );
      if (detailsItem.value && succeededPaths.has(detailsItem.value.path)) {
        closeDetails();
      }
    });
  };

  const state: FileManagerDeleteState = {
    isConfirmOpen,
    pendingTarget,
    deletingPaths,
    canDeleteAt,
    requestDelete,
    confirmDelete,
    cancelDelete,
  };
  provide(fileManagerDeleteKey, state);
  return state;
};

export const useFileManagerDelete = () => {
  const state = inject(fileManagerDeleteKey);
  if (!state) {
    throw new Error(
      'useFileManagerDelete() must be called within a component tree provided by provideFileManagerDelete()',
    );
  }
  return state;
};
